import React, { useState } from 'react';
import { Grid, Button as MuiButton, TextField } from '@mui/material';
import { JobCode, updateJobCode } from '../../../../core/API/scheduler';
import styles from '../SchedulerSetting.module.scss';

interface EditJobCodeProps {
	jobCode: JobCode;
	handleBackClick?: () => void;
	onJobCodeUpdated?: () => void;
}

interface JobCodeBuffer {
	jobNumber: string;
	label: string;
	color: string;
}

const EditJobCode: React.FC<EditJobCodeProps> = ({
	jobCode,
	handleBackClick,
	onJobCodeUpdated,
}) => {
	const [jobCodeBuffer, setJobCodeBuffer] = useState<JobCodeBuffer>({
		jobNumber: jobCode.jobNumber?.toString() || '',
		label: jobCode.label || '',
		color: jobCode.backgroundColor || '#FF0000',
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
			console.log('Submitting job code buffer:', jobCodeBuffer);

			if (!jobCodeBuffer.jobNumber) {
				setError('Job code number is required');
				return;
			}		if (!jobCodeBuffer.label.trim()) {
			setError('Job code name is required');
			return;
		}

			// Validate job code number is numeric and within 2 digits
			const jobNum = parseInt(jobCodeBuffer.jobNumber, 10);
			if (isNaN(jobNum) || jobNum < 0 || jobNum > 99) {
				setError('Job code number must be between 0 and 99');
				return;
			}		try {
			setIsSubmitting(true);
			await updateJobCode(jobCode.id, {
				...jobCode,
				jobNumber: parseInt(jobCodeBuffer.jobNumber, 10),
				label: jobCodeBuffer.label,
				backgroundColor: jobCodeBuffer.color,
			});

			onJobCodeUpdated?.();
			setTimeout(() => {
				handleBackClick?.();
			}, 500);
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : typeof err === 'string' ? err : 'Failed to update job code';
			setError(errorMessage);
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.editJobCode}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<MuiButton className={styles.backButton} onClick={handleBackClick} variant="contained">
						Back
					</MuiButton>
					<MuiButton
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isSubmitting}
						variant="contained"
					>
						{isSubmitting ? 'Saving...' : 'Save'}
					</MuiButton>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Edit Job Code</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<TextField
					label="Job Code Number"
					name="jobNumber"
					type="number"
					placeholder="0-99"
					value={jobCodeBuffer.jobNumber}
					onChange={(e) => {
						const value = e.target.value;
						// Restrict to maximum 2 digits
						if (value === '' || (/^\d{1,2}$/.test(value) && parseInt(value) <= 99)) {
							setJobCodeBuffer({ ...jobCodeBuffer, jobNumber: value });
						}
					}}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 0, max: 99, maxLength: 2 }}
				/>

				<TextField
					label="Job Code Name"
					name="label"
					value={jobCodeBuffer.label}
					onChange={(e) => setJobCodeBuffer({ ...jobCodeBuffer, label: e.target.value })}
					placeholder="Enter job code name"
					className={styles.input}
					margin="normal"
					fullWidth
					required
				/>

				<div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16 }}>
					<label className={styles.label} htmlFor="color-picker">
						Color
					</label>
					<input
						id="color-picker"
						type="color"
						value={jobCodeBuffer.color}
						onChange={(e) => setJobCodeBuffer({ ...jobCodeBuffer, color: e.target.value })}
						style={{
							width: 100,
							height: 40,
							borderRadius: 4,
							border: '1px solid #ccc',
							cursor: 'pointer',
						}}
					/>
				</div>
			</form>
		</div>
	);
};

export default EditJobCode;
